(() => {
  const TOOL_SCHEMAS = {
    site_search: {
      type: "object",
      properties: {
        q: { type: "string", description: "Search query" },
        type: { type: "string", description: "Optional Shopify search result type" }
      },
      required: ["q"]
    },
    filter_products: {
      type: "object",
      properties: {
        q: { type: "string", description: "Current search or collection query" },
        sort_by: { type: "string", description: "Sort option value" }
      },
      additionalProperties: true
    },
    "add-product-to-cart": {
      type: "object",
      properties: {
        id: { type: "string", description: "Shopify variant ID" },
        quantity: { type: "integer", minimum: 1, description: "Quantity to add" }
      }
    },
    "cart-checkout": {
      type: "object",
      properties: {
        note: { type: "string", description: "Optional order note" }
      }
    },
    "contact-support": {
      type: "object",
      properties: {
        "contact[name]": { type: "string", description: "Customer name" },
        "contact[email]": { type: "string", format: "email", description: "Customer email address" },
        "contact[body]": { type: "string", description: "Message body" }
      },
      required: ["contact[email]", "contact[body]"]
    },
    "newsletter-signup": {
      type: "object",
      properties: {
        "contact[email]": { type: "string", format: "email", description: "Email address to subscribe" }
      },
      required: ["contact[email]"]
    },
    "submit-blog-comment": {
      type: "object",
      properties: {
        "comment[author]": { type: "string", description: "Comment author name" },
        "comment[email]": { type: "string", format: "email", description: "Comment author email" },
        "comment[body]": { type: "string", description: "Comment body" }
      },
      required: ["comment[author]", "comment[email]", "comment[body]"]
    },
    "change-store-localization": {
      type: "object",
      properties: {
        country_code: { type: "string", description: "Country code" },
        locale_code: { type: "string", description: "Locale code" }
      }
    },
    "customer-login": {
      type: "object",
      properties: {
        "customer[email]": { type: "string", format: "email", description: "Customer email address" },
        "customer[password]": { type: "string", description: "Customer password" }
      },
      required: ["customer[email]", "customer[password]"]
    },
    "recover-customer-password": {
      type: "object",
      properties: {
        email: { type: "string", format: "email", description: "Customer email address" }
      },
      required: ["email"]
    },
    "create-customer-account": {
      type: "object",
      properties: {
        "customer[first_name]": { type: "string", description: "First name" },
        "customer[last_name]": { type: "string", description: "Last name" },
        "customer[email]": { type: "string", format: "email", description: "Email address" },
        "customer[password]": { type: "string", description: "Account password" }
      },
      required: ["customer[email]", "customer[password]"]
    },
    "activate-customer-account": {
      type: "object",
      properties: {
        "customer[password]": { type: "string", description: "New account password" },
        "customer[password_confirmation]": { type: "string", description: "Password confirmation" }
      },
      required: ["customer[password]", "customer[password_confirmation]"]
    },
    "reset-customer-password": {
      type: "object",
      properties: {
        "customer[password]": { type: "string", description: "New account password" },
        "customer[password_confirmation]": { type: "string", description: "Password confirmation" }
      },
      required: ["customer[password]", "customer[password_confirmation]"]
    },
    "storefront-password-login": {
      type: "object",
      properties: {
        password: { type: "string", description: "Storefront password" }
      },
      required: ["password"]
    },
    "create-customer-address": {
      type: "object",
      properties: {
        "address[first_name]": { type: "string", description: "First name" },
        "address[last_name]": { type: "string", description: "Last name" },
        "address[address1]": { type: "string", description: "Address line 1" },
        "address[city]": { type: "string", description: "City" },
        "address[country]": { type: "string", description: "Country" },
        "address[zip]": { type: "string", description: "Postal code" }
      },
      additionalProperties: true
    },
    "update-customer-address": {
      type: "object",
      properties: {
        "address[first_name]": { type: "string", description: "First name" },
        "address[last_name]": { type: "string", description: "Last name" },
        "address[address1]": { type: "string", description: "Address line 1" },
        "address[city]": { type: "string", description: "City" },
        "address[country]": { type: "string", description: "Country" },
        "address[zip]": { type: "string", description: "Postal code" }
      },
      additionalProperties: true
    },
    "delete-customer-address": {
      type: "object",
      properties: {
        confirm: { type: "boolean", description: "Confirmation to delete the address" }
      },
      required: ["confirm"]
    }
  };

  const SENSITIVE_TOOL_NAMES = new Set([
    "add-product-to-cart",
    "cart-checkout",
    "customer-login",
    "create-customer-account",
    "activate-customer-account",
    "reset-customer-password",
    "storefront-password-login",
    "create-customer-address",
    "update-customer-address",
    "delete-customer-address"
  ]);
  const REGISTERED_TOOL_NAMES = new Set();

  const ensureModelContext = () => {
    window.webMCPBridge = window.webMCPBridge || {};
    window.webMCPBridge.tools = window.webMCPBridge.tools || [];

    if (navigator.modelContext && typeof navigator.modelContext.registerTool === "function") {
      return navigator.modelContext;
    }

    const modelContext = {
      tools: window.webMCPBridge.tools,
      registerTool(tool) {
        const existingIndex = window.webMCPBridge.tools.findIndex((registeredTool) => registeredTool.name === tool.name);

        if (existingIndex >= 0) {
          window.webMCPBridge.tools[existingIndex] = tool;
        } else {
          window.webMCPBridge.tools.push(tool);
        }

        return tool;
      },
      async requestUserInteraction() {
        return true;
      }
    };

    try {
      Object.defineProperty(navigator, "modelContext", {
        configurable: true,
        value: modelContext
      });
    } catch (error) {
      navigator.modelContext = modelContext;
    }

    return navigator.modelContext;
  };

  const getModelContext = () => {
    ensureModelContext();

    if (typeof navigator.modelContext.registerTool !== "function") {
      return null;
    }

    return navigator.modelContext;
  };

  const getToolAttribute = (form, name) => {
    return form.getAttribute(name) || form.dataset[name] || "";
  };

  const getForms = (toolName) => {
    const selector = toolName
      ? `form[toolname="${CSS.escape(toolName)}"], form[data-toolname="${CSS.escape(toolName)}"]`
      : "form[toolname][tooldescription][toolaction], form[data-toolname][data-tooldescription][data-toolaction]";

    return Array.from(document.querySelectorAll(selector));
  };

  const getPreferredForm = (toolName) => {
    return getForms(toolName).find((form) => form.offsetParent !== null) || getForms(toolName)[0] || null;
  };

  const setInputValue = (form, name, value) => {
    const field = form.elements.namedItem(name);

    if (!field || value === undefined || value === null) {
      return;
    }

    if (field instanceof RadioNodeList) {
      Array.from(field).forEach((item) => {
        if ("checked" in item) {
          item.checked = item.value === String(value);
        }
      });
      return;
    }

    if (field.type === "checkbox") {
      field.checked = Boolean(value);
      return;
    }

    field.value = String(value);
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const fillForm = (form, input) => {
    Object.entries(input || {}).forEach(([name, value]) => setInputValue(form, name, value));

    if (input && input.email) {
      setInputValue(form, "contact[email]", input.email);
      setInputValue(form, "customer[email]", input.email);
      setInputValue(form, "comment[email]", input.email);
    }

    if (input && input.q) {
      setInputValue(form, "q", input.q);
    }
  };

  const requestAgentConsent = async (toolName, form) => {
    if (!SENSITIVE_TOOL_NAMES.has(toolName)) {
      return true;
    }

    const modelContext = navigator.modelContext;

    if (modelContext && typeof modelContext.requestUserInteraction === "function") {
      const result = await navigator.modelContext.requestUserInteraction({
        reason: getToolAttribute(form, "tooldescription") || `Confirm ${toolName}`
      });

      return result === undefined || result === true || Boolean(result && (result.confirmed === true || result.accepted === true));
    }

    return window.confirm(getToolAttribute(form, "tooldescription") || `Confirm ${toolName}`);
  };

  const submitForm = (form) => {
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
      return;
    }

    form.submit();
  };

  const invokeTool = async (toolName, input) => {
    const form = getPreferredForm(toolName);

    if (!form) {
      return { status: "unavailable", message: `No form found for ${toolName}.` };
    }

    fillForm(form, input || {});

    const approved = await requestAgentConsent(toolName, form);

    if (!approved) {
      return { status: "cancelled", message: "User interaction was not approved." };
    }

    submitForm(form);

    return { status: "submitted", toolName };
  };

  const registerTools = () => {
    const modelContext = getModelContext();

    if (!modelContext) {
      return;
    }

    getForms().forEach((form) => {
      const name = getToolAttribute(form, "toolname");

      if (!name || REGISTERED_TOOL_NAMES.has(name)) {
        return;
      }

      REGISTERED_TOOL_NAMES.add(name);

      navigator.modelContext.registerTool({
        name,
        description: getToolAttribute(form, "tooldescription") || name,
        inputSchema: TOOL_SCHEMAS[name] || { type: "object", additionalProperties: true },
        async handler(input) {
          return invokeTool(name, input);
        }
      });
    });
  };

  document.addEventListener(
    "submit",
    async (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;

      if (!form || !event.agentInvoked) {
        return;
      }

      const toolName = getToolAttribute(form, "toolname");

      if (!toolName || !SENSITIVE_TOOL_NAMES.has(toolName)) {
        return;
      }

      if (form.dataset.webmcpApproved === "true") {
        delete form.dataset.webmcpApproved;
        return;
      }

      event.preventDefault();

      const approved = await requestAgentConsent(toolName, form);

      if (!approved) {
        return;
      }

      form.dataset.webmcpApproved = "true";
      submitForm(form);
    },
    true
  );

  window.webMCPTools = {
    ensureModelContext,
    registerTools,
    invokeTool
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", registerTools, { once: true });
  } else {
    registerTools();
  }
})();
